import { inertHelper, isMobile } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import { Collapse } from 'react-collapse';
import { Key } from 'ts-key-enum';

import { AccordionItemHeader } from './AccordionItemHeader/AccordionItemHeader';

export interface ItemStatus {
    readonly isActive: boolean;
    readonly isExpanded: boolean;
}

interface IAccordionProps {
    readonly children: React.ReactNode;
    readonly expandedIcon?: React.ReactNode;
    readonly collapsedIcon?: React.ReactNode;
    readonly allowMultipleItems?: boolean;
    readonly showMoreIndicator?: React.ReactNode;
    readonly labelClassName?: string;
    readonly activeLabelClassName?: string;
    readonly itemsStatus?: ItemStatus[];
    readonly onActiveItemChanged?: (itemsStatus: ItemStatus[]) => void;
    readonly collapsedItemMaxHeight?: number | string;
    readonly className?: string;
    readonly itemClassName?: string;
    readonly enableAutoScrolling?: boolean;
    readonly scrollOnlyOnMobile?: boolean;
}

interface IAccordionState {
    readonly itemsStatus: ItemStatus[];
    readonly focusedItemIndex?: number;
    readonly itemsHeight: number[];
}

class Accordion extends React.Component<IAccordionProps, IAccordionState> {
    containerRef = React.createRef<HTMLDivElement>();

    constructor(props: IAccordionProps) {
        super(props);

        this.state = {
            itemsStatus: Accordion.getItemsStatus(props),
            itemsHeight: []
        };
    }

    componentDidMount() {
        this.setState({ itemsHeight: this.getItemsHeight() });
    }

    static getDerivedStateFromProps(props: IAccordionProps, state: IAccordionState) {
        return {
            itemsStatus: props.onActiveItemChanged ? Accordion.getItemsStatus(props) : state.itemsStatus
        };
    }

    static getItemsStatus = (props: IAccordionProps): ItemStatus[] => {
        return (
            React.Children.map(props.children, (child, i) => {
                let isActive = false;
                let isExpanded = false;

                if (props.itemsStatus) {
                    if (props.allowMultipleItems) {
                        isActive = props.itemsStatus[i] && props.itemsStatus[i].isActive;
                    } else {
                        isActive = props.itemsStatus[i].isActive;
                    }

                    isExpanded = props.itemsStatus[i] && props.itemsStatus[i].isExpanded;
                }

                return {
                    isActive: isActive,
                    isExpanded: isExpanded
                };
            }) || []
        );
    };

    private getItemsHeight = () => {
        if (this.containerRef.current) {
            const items = this.containerRef.current.querySelectorAll('.Accordion__ContentInner');
            const itemsHeight: number[] = [];

            Array.prototype.forEach.call(items, (item) => {
                itemsHeight.push((item as HTMLElement).offsetHeight);
            });

            return itemsHeight;
        }

        return [];
    };

    private setFocusedItemIndex = (index: number) => {
        this.setState({ focusedItemIndex: index });
    };

    private scrollToAccordionItem = (index: number) => {
        const elements = this.containerRef.current?.getElementsByClassName('Accordion__Item');
        if (elements && elements.length > 0) {
            const openedElement = elements[index];

            if (!openedElement || (this.props.scrollOnlyOnMobile && !isMobile())) return;

            setTimeout(() => openedElement.scrollIntoView({ behavior: 'smooth' }), 250);
        }
    };

    private openAccordionItem = (index: number) => {
        const newItemsStatus = this.state.itemsStatus.map((itemStatus, i) => {
            if (i === index) {
                if (this.props.enableAutoScrolling && !itemStatus.isActive) this.scrollToAccordionItem(index);

                return {
                    isExpanded: itemStatus.isActive || itemStatus.isExpanded,
                    isActive: !itemStatus.isActive
                };
            } else {
                if (this.props.allowMultipleItems) {
                    return { ...itemStatus };
                } else {
                    return {
                        // after reopen, item should be already expanded
                        isExpanded: itemStatus.isActive || itemStatus.isExpanded,
                        isActive: false
                    };
                }
            }
        });

        this.setState({ itemsStatus: newItemsStatus, focusedItemIndex: index });

        if (this.props.onActiveItemChanged) this.props.onActiveItemChanged(newItemsStatus);
    };

    private expandItemContent = (index: number) => {
        const newItemsStatus = this.state.itemsStatus.map((ItemStatus, i) => {
            return i === index ? { ...ItemStatus, isExpanded: true } : ItemStatus;
        });

        this.setState({ itemsStatus: newItemsStatus });
        if (this.props.onActiveItemChanged) this.props.onActiveItemChanged(newItemsStatus);
    };

    private handleControl = (e: React.KeyboardEvent) => {
        if (e.key === Key.ArrowDown) {
            this.goToNextItem();
            e.preventDefault();
        } else if (e.key === Key.ArrowUp) {
            this.goToPreviousItem();
            e.preventDefault();
        } else if (e.key === Key.Home) {
            this.goToFirstItem();
            e.preventDefault();
        } else if (e.key === Key.End) {
            this.goToLastItem();
            e.preventDefault();
        }
    };

    private goToNextItem = () => {
        if (this.state.focusedItemIndex !== undefined && this.state.focusedItemIndex + 1 < this.state.itemsStatus.length) {
            this.setFocusedItemIndex(this.state.focusedItemIndex + 1);
        } else {
            this.setFocusedItemIndex(0);
        }
    };

    private goToPreviousItem = () => {
        if (this.state.focusedItemIndex !== undefined && this.state.focusedItemIndex - 1 >= 0) {
            this.setFocusedItemIndex(this.state.focusedItemIndex - 1);
        } else {
            this.setFocusedItemIndex(this.state.itemsStatus.length - 1);
        }
    };

    private goToFirstItem = () => {
        this.setState({ focusedItemIndex: 0 });
    };

    private goToLastItem = () => {
        this.setState({ focusedItemIndex: this.state.itemsStatus.length - 1 });
    };

    render() {
        const {
            props: { children, showMoreIndicator, labelClassName, activeLabelClassName, expandedIcon, collapsedIcon, className, itemClassName },
            state: { itemsStatus, focusedItemIndex, itemsHeight }
        } = this;

        const collapsedItemMaxHeight = this.props.collapsedItemMaxHeight || 200;

        return (
            <div className={classNames('Accordion', className)} ref={this.containerRef}>
                {React.Children.map(
                    children,
                    // eslint-disable-next-line
                    (child: any, i: number) =>
                        child &&
                        itemsStatus[i] && (
                            <div className={classNames('Accordion__Item', itemsStatus[i].isActive && 'Accordion__Item--Active', itemClassName)}>
                                <AccordionItemHeader
                                    label={child.props.label}
                                    handleOpenItem={(index: number, e) => {
                                        this.openAccordionItem(index);
                                        child.props.onClick && child.props.onClick(e);
                                    }}
                                    index={i}
                                    isActive={itemsStatus[i].isActive}
                                    isExpanded={itemsStatus[i].isExpanded}
                                    isFocused={focusedItemIndex === i}
                                    onKeyDown={this.handleControl}
                                    labelClassName={classNames(labelClassName, child.props.className)}
                                    activeLabelClassName={activeLabelClassName}
                                    expandedIcon={expandedIcon}
                                    collapsedIcon={collapsedIcon}
                                />

                                <Collapse isOpened={itemsStatus[i].isActive}>
                                    <div
                                        className="Accordion__Content"
                                        {...inertHelper(itemsStatus[i].isActive)}
                                        style={{
                                            maxHeight: showMoreIndicator && !itemsStatus[i].isExpanded ? collapsedItemMaxHeight : 'unset'
                                        }}
                                    >
                                        <div className="Accordion__ContentInner">{child}</div>
                                    </div>

                                    {showMoreIndicator && (
                                        <div className="Accordion__ShowMorePlaceholder">
                                            {!itemsStatus[i].isExpanded && itemsStatus[i].isActive && itemsHeight[i] > collapsedItemMaxHeight && (
                                                <button className="Accordion__ExpandButton" onClick={() => this.expandItemContent(i)}>
                                                    {showMoreIndicator}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </Collapse>
                            </div>
                        )
                )}
            </div>
        );
    }
}

export default Accordion;
